// 牌庫操作效果處理器
// Deck Manipulation Effect Handlers

import type { GameState, Card } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('DeckHandlers');

/**
 * 展示牌庫頂效果處理器
 * 歡樂島核心機制
 */
export class RevealTopHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('展示') && (text.includes('牌庫頂') || text.includes('牌庫頂的'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「展示你牌庫頂X張牌」
    const revealMatch = effectText.match(/展示你?牌庫頂的?(\d*)張?牌/);
    if (revealMatch) {
      const count = revealMatch[1] ? parseInt(revealMatch[1], 10) : 1;
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'revealTop',
        value: count,
      });

      // 檢查後續效果
      if (effectText.includes('加入手牌')) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'revealToHand',
          value: count,
        });
      }

      if (effectText.includes('被額外打出')) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'revealToPlay',
        });
      }

      if (effectText.includes('被破壞')) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'revealToDestroy',
        });
      }
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    if (action.customHandler === 'revealTop') {
      const count = typeof action.value === 'number' ? action.value : 1;
      const deck = player.deck;
      const revealed = deck.slice(0, count);
      
      log.debug(`展示牌庫頂 ${String(count)} 張:`, revealed.map(c => c.definitionId));
      
      // 將展示的牌記錄到 state（供後續效果使用）
      return {
        ...state,
        revealedCards: revealed,
      } as GameState & { revealedCards: Card[] };
    }

    if (action.customHandler === 'revealToHand') {
      // 將展示的牌加入手牌
      const extendedState = state as GameState & { revealedCards?: Card[] };
      const revealed = extendedState.revealedCards ?? [];
      if (revealed.length === 0) return state;

      const deck = [...player.deck];
      const hand = [...player.hand];
      
      for (const card of revealed) {
        const idx = deck.findIndex(c => c.id === card.id);
        if (idx !== -1) {
          deck.splice(idx, 1);
          hand.push({ ...card, location: 'hand' });
        }
      }

      log.debug(`加入手牌 ${String(revealed.length)} 張`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          deck,
          hand,
        },
        revealedCards: undefined,
      } as GameState;
    }

    return state;
  }
}

/**
 * 搜尋牌庫效果處理器（增強版）
 */
export class SearchDeckHandlerV2 implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('從你牌庫尋找') || text.includes('從牌庫尋找')) ||
           (text.includes('從你手牌或牌庫尋找'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「從你牌庫尋找X張「卡名」加入手牌」
    const searchNameMatch = effectText.match(/從你?牌庫尋找(\d*)張?「([^」]+)」[^加]*加入手牌/);
    if (searchNameMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchByName',
        value: searchNameMatch[2],
        targetSelector: {
          zone: 'deck',
          side: 'self',
          count: searchNameMatch[1] ? parseInt(searchNameMatch[1], 10) : 1,
        },
      });
    }

    // 解析「從你牌庫尋找X種族單位加入手牌」
    const searchFactionMatch = effectText.match(/從你?牌庫尋找(\d*)張?([^\s單位]+)單位[^加]*加入手牌/);
    if (searchFactionMatch && !searchNameMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchByFaction',
        value: searchFactionMatch[2],
        targetSelector: {
          zone: 'deck',
          side: 'self',
          count: searchFactionMatch[1] ? parseInt(searchFactionMatch[1], 10) : 1,
        },
      });
    }

    // 解析「從你手牌或牌庫尋找X被召喚」
    const searchToSummonMatch = effectText.match(/從你手牌或牌庫尋找(\d*)([^\s並]+)[^並]*被召喚/);
    if (searchToSummonMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchToSummon',
        value: searchToSummonMatch[2],
        targetSelector: {
          zone: 'deck',
          side: 'self',
          count: searchToSummonMatch[1] ? parseInt(searchToSummonMatch[1], 10) : 1,
        },
      });
    }

    // 檢查是否需要重洗牌庫
    if (effectText.includes('重洗牌庫')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'shuffleDeck',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    if (action.customHandler === 'searchByName') {
      const cardName = action.value as string;
      const count = action.targetSelector?.count ?? 1;
      const deck = [...player.deck];
      const hand = [...player.hand];
      let found = 0;

      // 從牌庫尋找指定名稱的卡
      for (let i = deck.length - 1; i >= 0 && found < count; i--) {
        // 這裡需要對照 CardDefinition 來比對名稱
        // 簡化：假設 definitionId 包含卡名
        if (deck[i].definitionId.toLowerCase().includes(cardName.toLowerCase())) {
          const cards = deck.splice(i, 1);
          hand.push({ ...cards[0], location: 'hand' as const });
          found++;
        }
      }

      log.debug(`從牌庫尋找「${cardName}」: 找到 ${String(found)} 張`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          deck,
          hand,
        },
      };
    }

    if (action.customHandler === 'searchByFaction') {
      const faction = action.value as string;
      const count = action.targetSelector?.count ?? 1;
      const deck = [...player.deck];
      const hand = [...player.hand];
      let found = 0;

      for (let i = deck.length - 1; i >= 0 && found < count; i--) {
        if (deck[i].definitionId.toLowerCase().includes(faction.toLowerCase())) {
          const cards = deck.splice(i, 1);
          hand.push({ ...cards[0], location: 'hand' as const });
          found++;
        }
      }

      log.debug(`從牌庫尋找${faction}單位: 找到 ${String(found)} 張`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          deck,
          hand,
        },
      };
    }

    if (action.customHandler === 'shuffleDeck') {
      const deck = [...player.deck];
      
      // Fisher-Yates shuffle
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }

      log.debug(`重洗牌庫`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          deck,
        },
      };
    }

    return state;
  }
}

/**
 * 放回牌庫效果處理器
 */
export class ReturnToDeckHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('放回') && (text.includes('牌庫') || text.includes('牌庫頂'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你1張手牌被放回你牌庫頂」
    const returnToTopMatch = effectText.match(/使你(\d*)張?手牌被放回你?牌庫頂/);
    if (returnToTopMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnToTop',
        value: returnToTopMatch[1] ? parseInt(returnToTopMatch[1], 10) : 1,
        targetSelector: {
          zone: 'hand',
          side: 'self',
        },
      });
    }

    // 解析「從你牌庫尋找X放回你牌庫頂」
    const searchReturnMatch = effectText.match(/從你牌庫尋找(\d*)張?([^放]+)放回你?牌庫頂/);
    if (searchReturnMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchReturnToTop',
        value: searchReturnMatch[2].trim(),
        targetSelector: {
          zone: 'deck',
          side: 'self',
          count: searchReturnMatch[1] ? parseInt(searchReturnMatch[1], 10) : 1,
        },
      });
    }

    // 解析「使你墓地X被放回你牌庫頂」
    const graveyardReturnMatch = effectText.match(/使你墓地(\d*)張?([^被]+)?被放回你?牌庫頂/);
    if (graveyardReturnMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'graveyardReturnToTop',
        value: graveyardReturnMatch[2] ? graveyardReturnMatch[2].trim() : undefined,
        targetSelector: {
          zone: 'graveyard',
          side: 'self',
          count: graveyardReturnMatch[1] ? parseInt(graveyardReturnMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    if (action.customHandler === 'returnToTop') {
      const count = typeof action.value === 'number' ? action.value : 1;
      const hand = [...player.hand];
      const deck = [...player.deck];

      const toReturn = Math.min(count, hand.length);
      for (let i = 0; i < toReturn; i++) {
        const card = hand.pop();
        if (card) {
          deck.unshift({ ...card, location: 'deck' as const });
        }
      }

      log.debug(`放回牌庫頂 ${String(toReturn)} 張`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          hand,
          deck,
        },
      };
    }

    if (action.customHandler === 'graveyardReturnToTop') {
      const count = action.targetSelector?.count ?? 1;
      const cardType = action.value as string | undefined;
      const graveyard = [...player.graveyard];
      const deck = [...player.deck];

      let returned = 0;
      for (let i = graveyard.length - 1; i >= 0 && returned < count; i--) {
        // 簡化：如果有指定類型，檢查 definitionId
        if (!cardType || graveyard[i].definitionId.includes(cardType)) {
          const cards = graveyard.splice(i, 1);
          deck.unshift({ ...cards[0], location: 'deck' as const });
          returned++;
        }
      }

      log.debug(`墓地放回牌庫頂 ${String(returned)} 張`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          graveyard,
          deck,
        },
      };
    }

    return state;
  }
}

/**
 * 洗牌效果處理器
 */
export class ShuffleHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('重洗牌庫') || text.includes('洗回牌庫');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「將你X張手牌洗回牌庫」
    const shuffleBackMatch = effectText.match(/將你(\d*)張?手牌洗回牌庫/);
    if (shuffleBackMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'shuffleBack',
        value: shuffleBackMatch[1] ? parseInt(shuffleBackMatch[1], 10) : 1,
      });
    }

    // 單純重洗牌庫
    if (effectText.includes('重洗牌庫') && !effectText.includes('洗回牌庫')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'shuffle',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const player = state[playerKey];

    if (action.customHandler === 'shuffle' || action.customHandler === 'shuffleBack') {
      const deck = [...player.deck];
      const hand = [...player.hand];

      // 如果是洗回牌庫，先把手牌加入
      if (action.customHandler === 'shuffleBack') {
        const count = typeof action.value === 'number' ? action.value : 1;
        const toShuffle = Math.min(count, hand.length);
        for (let i = 0; i < toShuffle; i++) {
          const card = hand.pop();
          if (card) {
            deck.push({ ...card, location: 'deck' as const });
          }
        }
        log.debug(`將 ${String(toShuffle)} 張手牌洗回牌庫`);
      }

      // Fisher-Yates shuffle
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }

      log.debug(`重洗牌庫`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          deck,
          hand,
        },
      };
    }

    return state;
  }
}

// 匯出所有牌庫處理器
export const deckHandlers = {
  RevealTopHandler,
  SearchDeckHandlerV2,
  ReturnToDeckHandler,
  ShuffleHandler,
};
